// Landing Page Templates Configuration
// Multiple template designs for TaxFile Appointment system
// Companies can customize content from admin portal

// Default content shared across all templates - Complete mock data
const DEFAULT_CONTENT = {
  // Hero Section
  heroTitle: 'Professional Tax Preparation Services',
  heroSubtitle: 'Schedule your appointment with our experienced tax preparers. We provide reliable and accurate tax preparation services across all 50 states.',
  ctaText: 'Book an Appointment',
  ctaSecondary: 'Contact Us',
  heroBadge: '✓ Licensed & Certified',
  
  // About Section
  aboutTitle: 'About Us',
  aboutDescription: 'We have been proudly serving individuals and businesses since 2017. Our experienced team stays up to date with the latest tax laws and regulations to ensure your tax returns are prepared correctly and on time. We are committed to helping our clients maximize their refunds while staying fully compliant with IRS requirements.',
  aboutHighlights: [
    { text: 'Licensed Professionals', icon: '✓' },
    { text: 'Personalized Service', icon: '✓' },
    { text: 'Year-Round Support', icon: '✓' }
  ],
  
  // Services Section
  servicesTitle: 'Our Services',
  servicesSubtitle: 'Professional tax services tailored to your needs',
  services: [
    { 
      name: 'Individual Tax Returns', 
      description: 'Personal tax preparation for individuals and families',
      price: 'From $99', 
      icon: '📋',
      features: ['W-2 & 1099 Filing', 'Deduction Optimization', 'E-File Available']
    },
    { 
      name: 'Business Tax Returns', 
      description: 'Comprehensive tax services for businesses of all sizes',
      price: 'From $299', 
      icon: '🏢',
      features: ['LLC & S-Corp', 'Partnership Returns', 'Quarterly Filings']
    },
    { 
      name: 'Tax Planning', 
      description: 'Strategic planning to minimize your tax burden',
      price: 'From $149', 
      icon: '📊',
      features: ['Year-Round Advice', 'Investment Strategy', 'Retirement Planning']
    },
    { 
      name: 'Bookkeeping Services', 
      description: 'Keep your finances organized year-round',
      price: '$199/mo', 
      icon: '📚',
      features: ['Monthly Reports', 'Expense Tracking', 'Payroll Support']
    }
  ],
  
  // Features/Why Choose Us Section
  featuresTitle: 'Why Choose Us?',
  featuresSubtitle: 'What makes us different from others',
  features: [
    { 
      icon: '👨‍💼', 
      title: 'Expert Preparers', 
      description: 'Certified professionals with years of experience in tax preparation and planning' 
    },
    { 
      icon: '⚡', 
      title: 'Fast Service', 
      description: 'Quick and efficient tax preparation with same-day appointments available' 
    },
    { 
      icon: '🔒', 
      title: 'Secure Filing', 
      description: 'Your personal and financial information is always protected and confidential' 
    },
    { 
      icon: '💰', 
      title: 'Maximize Returns', 
      description: 'We find every deduction and credit you deserve to get your maximum refund' 
    },
    { 
      icon: '📱', 
      title: 'Easy Booking', 
      description: 'Schedule appointments online 24/7 at your convenience' 
    },
    { 
      icon: '💬', 
      title: 'Free Consultation', 
      description: 'Discuss your tax situation with us before committing to any service' 
    }
  ],
  
  // Team/Preparers Section
  teamTitle: 'Our Tax Preparers',
  teamSubtitle: 'Choose your preferred preparer to book an appointment',
  
  // Contact Section
  contactTitle: 'Contact Us',
  contactSubtitle: 'Have questions? Reach out to us.',
  contactEmail: 'contact@company.com',
  contactPhone: '(123) 456-7890',
  contactAddress: '123 Business Street, City, State 12345',
  businessHours: 'Mon-Fri: 9AM-6PM | Sat: 10AM-4PM',
  
  // Footer CTA Section
  footerCtaTitle: 'Ready to Get Started?',
  footerCtaSubtitle: 'Book an appointment with our expert team today!',
  footerCtaButton: 'Book Now →',
  
  // Testimonials (Optional)
  showTestimonials: true,
  testimonialsTitle: 'What Our Clients Say',
  testimonials: [
    { 
      text: 'Best tax service I\'ve ever used! They found deductions I didn\'t even know about and got me my maximum refund.', 
      name: 'John D.', 
      role: 'Individual Client',
      rating: 5 
    },
    { 
      text: 'Professional, fast, and affordable. They handle all my business taxes with expertise. Highly recommend!', 
      name: 'Sarah M.', 
      role: 'Small Business Owner',
      rating: 5 
    },
    { 
      text: 'The team is always available to answer questions. They make tax season stress-free!', 
      name: 'Mike R.', 
      role: 'Returning Client',
      rating: 5 
    }
  ],
  
  // Stats Section
  showStats: true,
  stats: [
    { value: '5000+', label: 'Clients Served' },
    { value: '8+', label: 'Years Experience' },
    { value: '50', label: 'States Covered' },
    { value: '98%', label: 'Client Satisfaction' }
  ],
  
  // Navigation Links
  navLinks: ['About', 'Services', 'Team', 'Contact'],
  
  // Social Links (placeholders)
  socialLinks: {
    facebook: '#',
    twitter: '#',
    linkedin: '#',
    instagram: '#'
  }
};

export const LANDING_PAGE_TEMPLATES = {
  // Template 1: Classic Blue (Default)
  'taxfile-classic': {
    id: 'taxfile-classic',
    name: 'Classic Professional',
    icon: '📊',
    description: 'Clean and professional blue theme - Traditional business look',
    preview: 'classic',
    category: 'TaxFile',
    layout: 'classic',
    ...DEFAULT_CONTENT,
    primaryColor: '#2563eb',
    secondaryColor: '#1e40af',
    accentColor: '#3b82f6',
    gradientStart: '#2563eb',
    gradientEnd: '#1e40af',
    heroStyle: 'gradient',
    cardStyle: 'shadow',
    buttonStyle: 'solid'
  },

  // Template 2: Modern Dark
  'taxfile-dark': {
    id: 'taxfile-dark',
    name: 'Modern Dark',
    icon: '🌙',
    description: 'Sleek dark theme with modern aesthetics',
    preview: 'dark',
    category: 'TaxFile',
    layout: 'modern',
    ...DEFAULT_CONTENT,
    primaryColor: '#1f2937',
    secondaryColor: '#111827',
    accentColor: '#10b981',
    gradientStart: '#1f2937',
    gradientEnd: '#000000',
    heroStyle: 'dark',
    cardStyle: 'glass',
    buttonStyle: 'glow'
  },

  // Template 3: Fresh Green
  'taxfile-green': {
    id: 'taxfile-green',
    name: 'Fresh Green',
    icon: '🌿',
    description: 'Fresh and trustworthy green theme',
    preview: 'green',
    category: 'TaxFile',
    layout: 'classic',
    ...DEFAULT_CONTENT,
    primaryColor: '#059669',
    secondaryColor: '#047857',
    accentColor: '#34d399',
    gradientStart: '#059669',
    gradientEnd: '#047857',
    heroStyle: 'gradient',
    cardStyle: 'bordered',
    buttonStyle: 'solid'
  },

  // Template 4: Royal Purple
  'taxfile-purple': {
    id: 'taxfile-purple',
    name: 'Royal Purple',
    icon: '👑',
    description: 'Elegant purple theme for premium services',
    preview: 'purple',
    category: 'TaxFile',
    layout: 'elegant',
    ...DEFAULT_CONTENT,
    primaryColor: '#7c3aed',
    secondaryColor: '#6d28d9',
    accentColor: '#a78bfa',
    gradientStart: '#7c3aed',
    gradientEnd: '#4c1d95',
    heroStyle: 'gradient',
    cardStyle: 'elegant',
    buttonStyle: 'gradient'
  },

  // Template 5: Warm Orange
  'taxfile-orange': {
    id: 'taxfile-orange',
    name: 'Warm Orange',
    icon: '🔥',
    description: 'Energetic and warm orange theme',
    preview: 'orange',
    category: 'TaxFile',
    layout: 'bold',
    ...DEFAULT_CONTENT,
    primaryColor: '#ea580c',
    secondaryColor: '#c2410c',
    accentColor: '#fb923c',
    gradientStart: '#ea580c',
    gradientEnd: '#9a3412',
    heroStyle: 'bold',
    cardStyle: 'shadow',
    buttonStyle: 'solid'
  },

  // Template 6: Ocean Teal
  'taxfile-teal': {
    id: 'taxfile-teal',
    name: 'Ocean Teal',
    icon: '🌊',
    description: 'Calm and professional teal theme',
    preview: 'teal',
    category: 'TaxFile',
    layout: 'modern',
    ...DEFAULT_CONTENT,
    primaryColor: '#0891b2',
    secondaryColor: '#0e7490',
    accentColor: '#22d3ee',
    gradientStart: '#0891b2',
    gradientEnd: '#155e75',
    heroStyle: 'wave',
    cardStyle: 'modern',
    buttonStyle: 'rounded'
  },

  // Template 7: Bold Red
  'taxfile-red': {
    id: 'taxfile-red',
    name: 'Bold Red',
    icon: '❤️',
    description: 'Bold and confident red theme',
    preview: 'red',
    category: 'TaxFile',
    layout: 'bold',
    ...DEFAULT_CONTENT,
    primaryColor: '#dc2626',
    secondaryColor: '#b91c1c',
    accentColor: '#f87171',
    gradientStart: '#dc2626',
    gradientEnd: '#991b1b',
    heroStyle: 'bold',
    cardStyle: 'shadow',
    buttonStyle: 'solid'
  },

  // Template 8: Minimal Light
  'taxfile-minimal': {
    id: 'taxfile-minimal',
    name: 'Minimal Light',
    icon: '✨',
    description: 'Clean minimal design with lots of white space',
    preview: 'minimal',
    category: 'TaxFile',
    layout: 'minimal',
    ...DEFAULT_CONTENT,
    primaryColor: '#374151',
    secondaryColor: '#1f2937',
    accentColor: '#6b7280',
    gradientStart: '#f9fafb',
    gradientEnd: '#f3f4f6',
    heroStyle: 'minimal',
    cardStyle: 'flat',
    buttonStyle: 'outline'
  },

  // ==================== NEW BOOTSTRAP-INSPIRED TEMPLATES ====================

  // Template 9: Bootstrap Primary
  'bootstrap-primary': {
    id: 'bootstrap-primary',
    name: 'Bootstrap Primary',
    icon: '🅱️',
    description: 'Classic Bootstrap blue theme - Clean & Professional',
    preview: 'bootstrap',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#0d6efd',
    secondaryColor: '#0a58ca',
    accentColor: '#0dcaf0',
    gradientStart: '#0d6efd',
    gradientEnd: '#0a58ca',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 10: Bootstrap Success
  'bootstrap-success': {
    id: 'bootstrap-success',
    name: 'Bootstrap Success',
    icon: '✅',
    description: 'Trustworthy green Bootstrap theme',
    preview: 'bootstrap-green',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#198754',
    secondaryColor: '#157347',
    accentColor: '#20c997',
    gradientStart: '#198754',
    gradientEnd: '#0f5132',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 11: Bootstrap Danger
  'bootstrap-danger': {
    id: 'bootstrap-danger',
    name: 'Bootstrap Bold',
    icon: '🔴',
    description: 'Bold red Bootstrap theme for impact',
    preview: 'bootstrap-red',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#dc3545',
    secondaryColor: '#b02a37',
    accentColor: '#fd7e14',
    gradientStart: '#dc3545',
    gradientEnd: '#a71d2a',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 12: Bootstrap Info
  'bootstrap-info': {
    id: 'bootstrap-info',
    name: 'Bootstrap Cyan',
    icon: '💎',
    description: 'Fresh cyan Bootstrap theme',
    preview: 'bootstrap-cyan',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#0dcaf0',
    secondaryColor: '#0aa2c0',
    accentColor: '#6f42c1',
    gradientStart: '#0dcaf0',
    gradientEnd: '#087990',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 13: Bootstrap Warning
  'bootstrap-warning': {
    id: 'bootstrap-warning',
    name: 'Bootstrap Gold',
    icon: '⚡',
    description: 'Warm golden Bootstrap theme',
    preview: 'bootstrap-gold',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#ffc107',
    secondaryColor: '#e0a800',
    accentColor: '#fd7e14',
    gradientStart: '#ffc107',
    gradientEnd: '#cc9a06',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 14: Bootstrap Indigo
  'bootstrap-indigo': {
    id: 'bootstrap-indigo',
    name: 'Bootstrap Indigo',
    icon: '💜',
    description: 'Premium indigo Bootstrap theme',
    preview: 'bootstrap-indigo',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#6610f2',
    secondaryColor: '#520dc2',
    accentColor: '#d63384',
    gradientStart: '#6610f2',
    gradientEnd: '#3d0a91',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // Template 15: Bootstrap Pink
  'bootstrap-pink': {
    id: 'bootstrap-pink',
    name: 'Bootstrap Pink',
    icon: '🌸',
    description: 'Elegant pink Bootstrap theme',
    preview: 'bootstrap-pink',
    category: 'Bootstrap',
    layout: 'bootstrap',
    ...DEFAULT_CONTENT,
    primaryColor: '#d63384',
    secondaryColor: '#ab296a',
    accentColor: '#6f42c1',
    gradientStart: '#d63384',
    gradientEnd: '#801f4f',
    heroStyle: 'bootstrap',
    cardStyle: 'bootstrap',
    buttonStyle: 'bootstrap'
  },

  // ==================== GRADIENT TEMPLATES ====================

  // Template 16: Gradient Sunrise
  'gradient-sunrise': {
    id: 'gradient-sunrise',
    name: 'Gradient Sunrise',
    icon: '🌅',
    description: 'Beautiful sunrise gradient - Warm & Inviting',
    preview: 'gradient-sunrise',
    category: 'Gradient',
    layout: 'gradient',
    ...DEFAULT_CONTENT,
    primaryColor: '#f59e0b',
    secondaryColor: '#ef4444',
    accentColor: '#fbbf24',
    gradientStart: '#f59e0b',
    gradientEnd: '#ef4444',
    heroStyle: 'gradient-multi',
    cardStyle: 'glass',
    buttonStyle: 'gradient'
  },

  // Template 17: Gradient Ocean
  'gradient-ocean': {
    id: 'gradient-ocean',
    name: 'Gradient Ocean',
    icon: '🌊',
    description: 'Deep ocean gradient - Professional & Calm',
    preview: 'gradient-ocean',
    category: 'Gradient',
    layout: 'gradient',
    ...DEFAULT_CONTENT,
    primaryColor: '#06b6d4',
    secondaryColor: '#3b82f6',
    accentColor: '#22d3ee',
    gradientStart: '#06b6d4',
    gradientEnd: '#3b82f6',
    heroStyle: 'gradient-multi',
    cardStyle: 'glass',
    buttonStyle: 'gradient'
  },

  // Template 18: Gradient Forest
  'gradient-forest': {
    id: 'gradient-forest',
    name: 'Gradient Forest',
    icon: '🌲',
    description: 'Natural forest gradient - Trust & Growth',
    preview: 'gradient-forest',
    category: 'Gradient',
    layout: 'gradient',
    ...DEFAULT_CONTENT,
    primaryColor: '#10b981',
    secondaryColor: '#059669',
    accentColor: '#34d399',
    gradientStart: '#10b981',
    gradientEnd: '#047857',
    heroStyle: 'gradient-multi',
    cardStyle: 'glass',
    buttonStyle: 'gradient'
  },

  // Template 19: Gradient Sunset
  'gradient-sunset': {
    id: 'gradient-sunset',
    name: 'Gradient Sunset',
    icon: '🌇',
    description: 'Vibrant sunset gradient - Creative & Bold',
    preview: 'gradient-sunset',
    category: 'Gradient',
    layout: 'gradient',
    ...DEFAULT_CONTENT,
    primaryColor: '#ec4899',
    secondaryColor: '#8b5cf6',
    accentColor: '#f472b6',
    gradientStart: '#ec4899',
    gradientEnd: '#8b5cf6',
    heroStyle: 'gradient-multi',
    cardStyle: 'glass',
    buttonStyle: 'gradient'
  },

  // Template 20: Gradient Night
  'gradient-night': {
    id: 'gradient-night',
    name: 'Gradient Night',
    icon: '🌙',
    description: 'Elegant night sky gradient - Sophisticated',
    preview: 'gradient-night',
    category: 'Gradient',
    layout: 'gradient',
    ...DEFAULT_CONTENT,
    primaryColor: '#6366f1',
    secondaryColor: '#1e1b4b',
    accentColor: '#a5b4fc',
    gradientStart: '#6366f1',
    gradientEnd: '#1e1b4b',
    heroStyle: 'gradient-multi',
    cardStyle: 'glass',
    buttonStyle: 'gradient'
  },

  // ==================== MODERN UI TEMPLATES ====================

  // Template 21: Modern Glass
  'modern-glass': {
    id: 'modern-glass',
    name: 'Modern Glass',
    icon: '🔮',
    description: 'Glassmorphism design - Trendy & Modern',
    preview: 'modern-glass',
    category: 'Modern',
    layout: 'glass',
    ...DEFAULT_CONTENT,
    primaryColor: '#8b5cf6',
    secondaryColor: '#6366f1',
    accentColor: '#c4b5fd',
    gradientStart: '#8b5cf6',
    gradientEnd: '#6366f1',
    heroStyle: 'glass',
    cardStyle: 'glass',
    buttonStyle: 'glass'
  },

  // Template 22: Modern Neumorphism
  'modern-neumorphism': {
    id: 'modern-neumorphism',
    name: 'Modern Soft',
    icon: '☁️',
    description: 'Soft neumorphic design - Clean & Elegant',
    preview: 'modern-soft',
    category: 'Modern',
    layout: 'neumorphism',
    ...DEFAULT_CONTENT,
    primaryColor: '#6366f1',
    secondaryColor: '#4f46e5',
    accentColor: '#818cf8',
    gradientStart: '#e5e7eb',
    gradientEnd: '#f3f4f6',
    heroStyle: 'soft',
    cardStyle: 'neumorphism',
    buttonStyle: 'neumorphism'
  },

  // Template 23: Modern Brutalist
  'modern-brutalist': {
    id: 'modern-brutalist',
    name: 'Modern Brutalist',
    icon: '🏗️',
    description: 'Bold brutalist design - Strong & Impactful',
    preview: 'modern-brutalist',
    category: 'Modern',
    layout: 'brutalist',
    ...DEFAULT_CONTENT,
    primaryColor: '#000000',
    secondaryColor: '#1f2937',
    accentColor: '#fbbf24',
    gradientStart: '#000000',
    gradientEnd: '#1f2937',
    heroStyle: 'brutalist',
    cardStyle: 'brutalist',
    buttonStyle: 'brutalist'
  },

  // ==================== CORPORATE TEMPLATES ====================

  // Template 24: Corporate Blue
  'corporate-blue': {
    id: 'corporate-blue',
    name: 'Corporate Blue',
    icon: '🏛️',
    description: 'Professional corporate blue - Enterprise Ready',
    preview: 'corporate-blue',
    category: 'Corporate',
    layout: 'corporate',
    ...DEFAULT_CONTENT,
    primaryColor: '#1e40af',
    secondaryColor: '#1e3a8a',
    accentColor: '#3b82f6',
    gradientStart: '#1e40af',
    gradientEnd: '#1e3a8a',
    heroStyle: 'corporate',
    cardStyle: 'corporate',
    buttonStyle: 'corporate'
  },

  // Template 25: Corporate Navy
  'corporate-navy': {
    id: 'corporate-navy',
    name: 'Corporate Navy',
    icon: '⚓',
    description: 'Deep navy corporate theme - Executive Class',
    preview: 'corporate-navy',
    category: 'Corporate',
    layout: 'corporate',
    ...DEFAULT_CONTENT,
    primaryColor: '#0f172a',
    secondaryColor: '#020617',
    accentColor: '#38bdf8',
    gradientStart: '#0f172a',
    gradientEnd: '#020617',
    heroStyle: 'corporate',
    cardStyle: 'corporate',
    buttonStyle: 'corporate'
  },

  // Template 26: Corporate Slate
  'corporate-slate': {
    id: 'corporate-slate',
    name: 'Corporate Slate',
    icon: '🪨',
    description: 'Sophisticated slate gray - Modern Professional',
    preview: 'corporate-slate',
    category: 'Corporate',
    layout: 'corporate',
    ...DEFAULT_CONTENT,
    primaryColor: '#475569',
    secondaryColor: '#334155',
    accentColor: '#94a3b8',
    gradientStart: '#475569',
    gradientEnd: '#1e293b',
    heroStyle: 'corporate',
    cardStyle: 'corporate',
    buttonStyle: 'corporate'
  },

  // ==================== CREATIVE TEMPLATES ====================

  // Template 27: Creative Neon
  'creative-neon': {
    id: 'creative-neon',
    name: 'Creative Neon',
    icon: '💡',
    description: 'Vibrant neon colors - Eye-catching & Fun',
    preview: 'creative-neon',
    category: 'Creative',
    layout: 'creative',
    ...DEFAULT_CONTENT,
    primaryColor: '#22d3ee',
    secondaryColor: '#06b6d4',
    accentColor: '#a855f7',
    gradientStart: '#22d3ee',
    gradientEnd: '#a855f7',
    heroStyle: 'neon',
    cardStyle: 'neon',
    buttonStyle: 'neon'
  },

  // Template 28: Creative Retro
  'creative-retro': {
    id: 'creative-retro',
    name: 'Creative Retro',
    icon: '📼',
    description: 'Vintage retro vibes - Nostalgic & Warm',
    preview: 'creative-retro',
    category: 'Creative',
    layout: 'creative',
    ...DEFAULT_CONTENT,
    primaryColor: '#c2410c',
    secondaryColor: '#9a3412',
    accentColor: '#fbbf24',
    gradientStart: '#c2410c',
    gradientEnd: '#713f12',
    heroStyle: 'retro',
    cardStyle: 'retro',
    buttonStyle: 'retro'
  },

  // Template 29: Creative Pastel
  'creative-pastel': {
    id: 'creative-pastel',
    name: 'Creative Pastel',
    icon: '🎨',
    description: 'Soft pastel colors - Friendly & Approachable',
    preview: 'creative-pastel',
    category: 'Creative',
    layout: 'creative',
    ...DEFAULT_CONTENT,
    primaryColor: '#f0abfc',
    secondaryColor: '#e879f9',
    accentColor: '#a5f3fc',
    gradientStart: '#f0abfc',
    gradientEnd: '#c4b5fd',
    heroStyle: 'pastel',
    cardStyle: 'pastel',
    buttonStyle: 'pastel'
  },

  // ==================== NATURE TEMPLATES ====================

  // Template 30: Nature Earth
  'nature-earth': {
    id: 'nature-earth',
    name: 'Nature Earth',
    icon: '🌍',
    description: 'Earthy natural tones - Sustainable & Trustworthy',
    preview: 'nature-earth',
    category: 'Nature',
    layout: 'nature',
    ...DEFAULT_CONTENT,
    primaryColor: '#78716c',
    secondaryColor: '#57534e',
    accentColor: '#84cc16',
    gradientStart: '#78716c',
    gradientEnd: '#44403c',
    heroStyle: 'nature',
    cardStyle: 'nature',
    buttonStyle: 'nature'
  },

  // Template 31: Nature Sky
  'nature-sky': {
    id: 'nature-sky',
    name: 'Nature Sky',
    icon: '☁️',
    description: 'Sky blue natural theme - Open & Trustworthy',
    preview: 'nature-sky',
    category: 'Nature',
    layout: 'nature',
    ...DEFAULT_CONTENT,
    primaryColor: '#0ea5e9',
    secondaryColor: '#0284c7',
    accentColor: '#38bdf8',
    gradientStart: '#0ea5e9',
    gradientEnd: '#0369a1',
    heroStyle: 'nature',
    cardStyle: 'nature',
    buttonStyle: 'nature'
  },

  // Template 32: Nature Leaf
  'nature-leaf': {
    id: 'nature-leaf',
    name: 'Nature Leaf',
    icon: '🍃',
    description: 'Fresh leaf green - Natural & Growing',
    preview: 'nature-leaf',
    category: 'Nature',
    layout: 'nature',
    ...DEFAULT_CONTENT,
    primaryColor: '#22c55e',
    secondaryColor: '#16a34a',
    accentColor: '#4ade80',
    gradientStart: '#22c55e',
    gradientEnd: '#15803d',
    heroStyle: 'nature',
    cardStyle: 'nature',
    buttonStyle: 'nature'
  }
};

// Get all templates as array
export const getTemplatesList = () => {
  return Object.values(LANDING_PAGE_TEMPLATES);
};

// Get template by ID
export const getTemplateById = (id) => {
  return LANDING_PAGE_TEMPLATES[id] || LANDING_PAGE_TEMPLATES['taxfile-classic'];
};

// Get templates by category
export const getTemplatesByCategory = (category) => {
  return Object.values(LANDING_PAGE_TEMPLATES).filter(t => t.category === category);
};

// Get all categories
export const getCategories = () => {
  const categories = new Set(Object.values(LANDING_PAGE_TEMPLATES).map(t => t.category));
  return Array.from(categories);
};

// Default landing page settings based on template
export const getDefaultLandingPageSettings = (templateId, companyData = {}) => {
  const template = getTemplateById(templateId);
  
  return {
    templateId: template.id,
    heroTitle: companyData.name ? `Welcome to ${companyData.name}` : template.heroTitle,
    heroSubtitle: template.heroSubtitle,
    ctaText: template.ctaText,
    features: template.features,
    services: template.services,
    testimonials: template.testimonials,
    showFeatures: true,
    showServices: true,
    showTestimonials: true,
    showTeam: true,
    primaryColor: companyData.primaryColor || template.primaryColor,
    secondaryColor: companyData.secondaryColor || template.secondaryColor,
    // Company info
    companyName: companyData.name || '',
    companyEmail: companyData.email || '',
    companyPhone: companyData.phone || '',
    companyAddress: companyData.address || '',
    companyLogo: companyData.logo || ''
  };
};
